// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportViewerToolbarActions.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
// </copyright>
// ----------------------------------------------------------------------

/* eslint-disable no-unused-vars */

import { compose, withHandlers, mapProps, withProps } from 'recompose';
import EmailPopup from 'share/components/controls/CommonPopup/Izenda.EmailPopup';
import ReportUtils from 'logic/models/Izenda.ReportUtils';
import { EMAIL_TYPE, DELIVERY_METHOD } from 'logic/data/Izenda.EmailConstants';
import reportDesignerConstants from 'logic/data/Izenda.ReportDesignerConstants';
import CommonMessages from 'share/messages/Izenda.CommonMessages';
import constants from 'logic/data/Izenda.ReportsConstants';
import localizer from 'share/localization/Izenda.Localizer';
import { accessContext as exposeMethodAction } from 'pages/Izenda.ExposeMethodAction';
import { get, getOr, omit, invoke, isBoolean, curry } from 'lodash/fp';
import IntegrationStyleAction from 'pages/Izenda.IntegrationStyleAction';
import uiState from 'logic/Izenda.UiState';
import { ACCESS_RIGHT } from 'logic/data/Izenda.AuthorizationConstants';
import SubscriptionDetails from 'logic/models/Izenda.SubscriptionDetails';
import { checkFilterRule } from 'share/utils/Izenda.ReportPartUtils';

const integrationStyleAction = IntegrationStyleAction.checkDisplayByKey('hideToolbar');
/**
 * Open Email Popup
 * @param {Object} props
 * @param {Object} e
 * @returns {Object}
 */
const openEmailPopup = (props: any, e: any): any => {
  const { designerStore, boardDetail, localization, store } = props;
  const data = {
    reportId: boardDetail.id
  };
  const reportFilter = getOr(null, 'fieldStore.model.reportFilter', props)
  const subscriptionData = new SubscriptionDetails(data);
  const handleCloseEmailPopup = (email: string): any => {
    if (!email) {
      return false;
    }
    email.subscriptionFilterFields = subscriptionData.subscriptionFilterFields;
    email.subscriptionCommonFilterFields = subscriptionData.subscriptionCommonFilterFields;

    return designerStore
      .savePrintDraft()
      .get('id')
      .then(
        (prinDraftId: string): any =>
          ReportUtils.checkRequiredFilters(designerStore, prinDraftId).then(
            (isValidFilter: boolean): any => {
              if (!isValidFilter) {
                return localization.getText('REPORTS_CONSTANTS_REPORT_MESSAGE_REQUIRED_FILTER_EMAIL');
              }
              return store.sendEmail(prinDraftId, email, props.reportId);
            }
          )
      );
  };

  props.openPopup(EmailPopup, {
    onClosed: handleCloseEmailPopup,
    type: EMAIL_TYPE.report,
    reportId: boardDetail.id,
    reportFilter: reportFilter,
    reportState: reportDesignerConstants.REPORT_STATE.SAVED,
    data: subscriptionData
  });
};

/**
 * Update Results
 * @param {Object} props
 * @param {Object} _
 * @returns {Object}
 */
const updateResults = (props: any, _: any): any => {
  const { updateReportParts, fieldStore } = props;
  invoke('reportFilters.resetUpdateResult', fieldStore);
  fieldStore.setIgnoreCache();
  return updateReportParts(true);
};

/**
 * Show Filter Warning Message
 * @param {Object} props
 * @returns {boolean}
 */
const showFilterWarningMessage = (props: any): boolean => {
  const warningMessage = props.fieldStore && props.fieldStore.getUnspecifiedFilterWarning();
  return warningMessage
    ? props.confirmFn('Information', {
        type: CommonMessages.MESSAGE_TYPE.INFO_ACCEPT,
        description: warningMessage,
        showCancel: false
      })
    : Promise.resolve(false);
};

/**
 * Save report
 * @param {Object} props
 * @returns {Object}
 */
const saveReport = (props: any): any => {
  const {
    fieldStore,
    loader,
    validation,
    reportSubscriptionStore,
    location: {
      query: { inheritFilter }
    }
  } = props;
  const reportDesignerStore = fieldStore.reportStore;

  reportDesignerStore.model.setIgnoreCheckChange();

  loader.startLoading();
  return updateResults(props, null)
    .then(
      (result: any) =>
        result &&
        Promise.join(
          reportDesignerStore.saveReport(reportDesignerConstants.PAGE_NAME.FIELDS, true /* save from viewer*/),
         // reportSubscriptionStore.saveInstances(),
          function(saveReportResult, saveSubscriptionResult) {
            if (saveReportResult.messages && saveReportResult.messages.length > 0) {
              let messageKey = saveReportResult.messages[0].key;

              props
                .confirmFn('Information', {
                  description: saveReportResult.messages[0].messages[0],
                  type: CommonMessages.MESSAGE_TYPE.INFO,
                  showCloseIndicator: true,
                  isHTMLFormat: true
                })
                .then(() => {
                  if (messageKey === reportDesignerConstants.MESSAGE_KEY.CONCURRENCY) {
                    return props
                      .confirmFn('Confirmation', {
                        description: localizer.getText('COMMON_MESSAGES_CONFIRM_CHANGES_LOST')
                      })
                      .then(() => props.cancelReport());
                  }
                });
            }
            validation.reset();
            const success = saveReportResult.success && saveSubscriptionResult.success;

            success && fieldStore.getFilterData();
            if (saveSubscriptionResult.success) {
              reportSubscriptionStore.loadInstances();
            }

            // Update Error for report
            if (!saveReportResult.success) {
              validation.updateErrors(saveReportResult.messages);
            }
            return success;
          }
        )
    )
    .then(
      (result: any): any => {
        inheritFilter && props.resetReport();
        return result ? showFilterWarningMessage(props).then((): any => result) : result;
      }
    )
    .finally(loader.endLoading.bind(loader));
};

/**
 * Save Report Designer:
 *  - Validate data > if data valid:
 *    + open Save Popup for new Report
 *    + only save for existing Report
 * @param {Object} props
 * @param {Object} saveDesign
 * @returns {Object}
 */
const onSave = (props: any, saveDesign: any): any => {
  const { store, fieldStore } = props;
  const {
    reportFilters,
    reportStore,
    model: { reportBody }
  } = fieldStore;

  reportStore.canSaveAs = false;

  // Reset alert message
  reportStore.alertDetails.set({
    message: CommonMessages.EMPTY_MESSAGE.message
  });

  // Filter Validation
  const checkFilterMessage = checkFilterRule(reportFilters);

  if (checkFilterMessage) {
    return props.confirmFn('Information', {
      description: checkFilterMessage,
      type: 'info',
      isHTMLFormat: true,
      showCloseIndicator: true
    });
  }
  const allowToSave = get('masterModel.allowToSave', fieldStore);

  if (checkDirty(props) || (isBoolean(saveDesign) && !!saveDesign) || allowToSave) {
    if (reportBody.hasInvisibleFields) {
      const object = constants.reportTypeName(store.reportType);
      const message = String.format(constants.MESSAGES.SAVE_REPORT, object);

      return props
        .confirmFn('Confirmation', {
          description: message
        })
        .then(saveReport.bind(null, props), () => reportBody.markVisibleAllField().then(saveReport.bind(null, props)));
    }

    return saveReport(props);
  } else {
    reportStore.alertDetails.set({
      success: true,
      message: localizer.getText('COMMON_MESSAGES_NO_CHANGES_FOUND')
    });
    return Promise.resolve(false);
  }
};

/**
 * Check Dirty
 * @param {Object} props
 * @param {Object} e
 * @returns {boolean}
 */
const checkDirty = (props: any, _: any): boolean => {
  const {
    reportSubscriptionStore,
    fieldStore: {
      reportStore,
      reportStore: {
        model: { accessPriority }
      }
    },
    authorization,
    authorizationConstants,
    boardDetail
  } = props;
  const savePermission =
    (authorization.checkPrivileges([authorizationConstants.PERMISSION_KEY.ReportsActionsOverwriteExistingReport]) &&
      accessPriority === ACCESS_RIGHT.FullAccess &&
      !boardDetail.isGlobal) ||
    (boardDetail.isGlobal && uiState.currentUser.systemAdmin && !uiState.tenantId);
  if (savePermission) {
    return reportStore.model.checkDirty();
    // || reportSubscriptionStore.model.checkDirty();
  } else {
    return false;
  }
};

/**
 * Handle click on Show/Hide header & footer button
 * @param {Object} props
 * @param {Object} e
 */
const onDisplayShowHideHeaderFooter = (props: any, e: any): void => {
  const { designerStore } = props;
  designerStore && (designerStore.isShowingHeaderFooter = !designerStore.isShowingHeaderFooter);
};

/**
 * Resolve label of Show/Hide header & footer button
 * @param {Object} designerStore
 * @param {Object} localization
 * @returns {string} The label of Show/Hide Header and Footer button
 */
const getShowHideHeaderFooterLabel = ({ isShowingHeaderFooter }, localization: any): string => {
  const key = isShowingHeaderFooter
    ? 'COMMON_LABEL_HIDE_REPORT_HEADER_FOOTER'
    : 'COMMON_LABEL_SHOW_REPORT_HEADER_FOOTER';
  return localization.getText(key);
};

/**
 * Check to display Show/Hide header & footer button
 * @param {Object} fieldStore
 * @returns {boolean} Indicate to display the Show/Hide Header and Footer button
 */
const checkDisplayShowHideHeaderFooter = (fieldStore: any): boolean => {
  return get('masterModel.reportFormats.headerFooter.visible', fieldStore);
};

const ReportViewerToolbarActions = compose(
  withHandlers({
    openEmailPopup: curry(openEmailPopup),
    onSave: curry(onSave),
    checkDirty: curry(checkDirty),
    updateResults: curry(updateResults),
    onDisplayShowHideHeaderFooter: curry(onDisplayShowHideHeaderFooter)
  }),
  withProps({
    getShowHideHeaderFooterLabel,
    checkDisplayShowHideHeaderFooter
  }),
  exposeMethodAction,
  mapProps((props: any): any => omit(['unregisterExposeMethod', 'registerExposeMethod'], props)),
  integrationStyleAction
);

export default ReportViewerToolbarActions;



// WEBPACK FOOTER //
// ./ui/modules/Reports/ReportViewer/Izenda.ReportViewerToolbarActions.js